import { useEffect, useRef, useState } from "react";

const SHOW_INSTALL_PROMPT = "showInstallPrompt";

interface BeforeInstallPromptEvent extends Event {
  prompt: () => Promise<void>;
  userChoice: Promise<{ outcome: "accepted" | "dismissed"; platform: string }>;
}

export function useInstallPrompt() {
  const [deferredPrompt, setDeferredPrompt] = useState<BeforeInstallPromptEvent | null>(null);
  const [showPrompt, setShowPrompt] = useState(false);
  // Ref to keep track of previous showPrompt value
  const prevShowPrompt = useRef(showPrompt);

  // Listen for the beforeinstallprompt event
  useEffect(() => {
    const handler = (e: Event) => {
      e.preventDefault();
      setDeferredPrompt(e as BeforeInstallPromptEvent);
      // Only show if not explicitly disabled
      if (localStorage.getItem(SHOW_INSTALL_PROMPT) !== "false") {
        setShowPrompt(true);
      }
    };

    window.addEventListener("beforeinstallprompt", handler);
    return () => window.removeEventListener("beforeinstallprompt", handler);
  }, []);

  // When showPrompt transitions from true to false, save to localStorage
  useEffect(() => {
    const wasVisible = prevShowPrompt.current;
    if (wasVisible && !showPrompt) {
      localStorage.setItem(SHOW_INSTALL_PROMPT, "false");
    }
    // Update ref for next render
    prevShowPrompt.current = showPrompt;
  }, [showPrompt]);

  const promptInstall = async () => {
    if (!deferredPrompt) return;
    deferredPrompt.prompt();
    const result = await deferredPrompt.userChoice;
    // Hide prompt regardless of choice
    setShowPrompt(false);
    setDeferredPrompt(null);
  };

  const dismissPrompt = () => {
    // Immediately hide and persist choice
    localStorage.setItem(SHOW_INSTALL_PROMPT, "false");
    setShowPrompt(false);
  };

  return { showPrompt, setShowPrompt, promptInstall, dismissPrompt };
}
